import React from "react";
import { AlertCircle, CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/Button";

import { TONE } from "../constants";
import Chip from "./Chip";

// The Permissions column for one database.
//
// The platform database is the source of the catalog, never a sync target, so
// it reports the size of the catalog rather than a drift state. A drifted row
// reports the diff as `+n ~n -n`, which is the shape the sync modal repeats.
//
// `onClick` opens the detail drawer, which names the codes behind those
// numbers. It is only passed for rows that have a diff to name — the platform
// row has none, and an unreadable one has nothing to fetch — so the cell stays
// a static chip everywhere a click would open an empty drawer.
const PermissionsCell = ({ state, status, platformTotal, onClick }) => {
    if (state === "source") {
        return (
            <Chip
                tone={TONE.slate}
                label={`source · ${platformTotal ?? "?"}`}
                title="This database holds the permission catalog every other one is synced from."
            />
        );
    }
    if (state === "unknown") {
        return (
            <Chip
                tone={TONE.slate}
                icon={<AlertCircle size={11} />}
                label="unknown"
                title={
                    status?.error ||
                    "The permission catalog on this database could not be read."
                }
            />
        );
    }

    const chip =
        state === "current" ? (
            <Chip
                tone={TONE.emerald}
                icon={<CheckCircle2 size={11} />}
                label="in sync"
                title="This database carries the same permission catalog as the platform. Open to see the catalog it holds."
            />
        ) : (
            <Chip
                tone={TONE.amber}
                icon={<AlertCircle size={11} />}
                label={driftLabel(status)}
                mono
                title={`${status?.addedCount ?? 0} to add, ${
                    status?.updatedCount ?? 0
                } to update, ${
                    status?.removedCount ?? 0
                } to remove. Open to see which codes.`}
            />
        );

    if (!onClick) return chip;
    return (
        <Button
            onClick={onClick}
            className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-tone-violet/60 cursor-pointer"
        >
            {chip}
        </Button>
    );
};

// `+3 ~1 -2` — only the non-zero parts, so the shape stays short enough for a
// table cell. "drift" is the fallback for a row the server says is out of sync
// without a count to show, which should not happen and must still read as
// something.
const driftLabel = (status) => {
    const parts = [];
    if (status?.addedCount) parts.push(`+${status.addedCount}`);
    if (status?.updatedCount) parts.push(`~${status.updatedCount}`);
    if (status?.removedCount) parts.push(`-${status.removedCount}`);
    return parts.join(" ") || "drift";
};

export default PermissionsCell;
